import type { ApiList } from "./types";

const TOKEN_KEY = "saheltech_backoffice_token";
const ORG_KEY = "saheltech_active_org";

export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(status: number, payload: unknown) {
    const details = typeof payload === "object" && payload
      ? Object.entries(payload).flatMap(([field, value]) => {
        const messages = Array.isArray(value) ? value : [value];
        return messages.filter((item) => item != null).map((item) =>
          field === "detail" ? String(item) : `${field} : ${String(item)}`
        );
      })
      : [];
    const message = details.join(" · ") || `Erreur API (${status})`;
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

export const authStore = {
  getToken: () => sessionStorage.getItem(TOKEN_KEY) || "",
  setToken: (token: string) => sessionStorage.setItem(TOKEN_KEY, token),
  clearToken: () => sessionStorage.removeItem(TOKEN_KEY),
  getOrganisation: () => localStorage.getItem(ORG_KEY) || "",
  setOrganisation: (code: string) => localStorage.setItem(ORG_KEY, code),
  clearOrganisation: () => localStorage.removeItem(ORG_KEY)
};

function csrfCookie() {
  const pair = document.cookie.split("; ").find((value) => value.startsWith("csrftoken="));
  return pair ? decodeURIComponent(pair.slice("csrftoken=".length)) : "";
}

export async function logoutSession() {
  const token = csrfCookie();
  const response = await fetch("/api/v1/auth/oidc/logout/", {
    method: "POST",
    credentials: "same-origin",
    headers: token ? { "X-CSRFToken": token } : {}
  });
  if (!response.ok) throw new ApiError(response.status, await parseResponse(response));
}

async function parseResponse(response: Response) {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return response.json();
  const text = await response.text();
  return text ? { detail: text } : {};
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit & { organisation?: string | null } = {}
): Promise<T> {
  const token = authStore.getToken();
  const organisation =
    options.organisation === null
      ? ""
      : options.organisation || authStore.getOrganisation();

  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Token ${token}`);
  if (organisation) headers.set("X-Organisation-ID", organisation);
  if (options.method && !["GET", "HEAD", "OPTIONS"].includes(options.method.toUpperCase())) {
    const csrf = csrfCookie();
    if (csrf) headers.set("X-CSRFToken", csrf);
  }

  const response = await fetch(path, { ...options, headers, credentials: "same-origin" });
  const payload = await parseResponse(response);
  if (!response.ok) throw new ApiError(response.status, payload);
  return payload as T;
}

export async function exchangeOnboarding(handoff: string) {
  const response = await fetch("/api/v1/enterprises/onboarding/exchange/", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ token: handoff })
  });
  const payload = await parseResponse(response);
  if (!response.ok) throw new ApiError(response.status, payload);
  const data = payload as { token: string; organisation: string };
  authStore.setToken(data.token);
  authStore.setOrganisation(data.organisation);
  return data;
}

export async function login(username: string, password: string) {
  const response = await fetch("/api/v1/auth/token/", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ username, password })
  });
  const payload = await parseResponse(response);
  if (!response.ok) throw new ApiError(response.status, payload);
  const token = String((payload as { token: string }).token);
  authStore.setToken(token);
  return token;
}

export function rows<T>(payload: ApiList<T>): T[] {
  return Array.isArray(payload) ? payload : payload.results || [];
}

export const api = {
  get: <T>(path: string, organisation?: string | null) =>
    apiRequest<T>(path, { organisation }),
  post: <T>(path: string, body: unknown, organisation?: string | null) =>
    apiRequest<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
      organisation
    }),
  patch: <T>(path: string, body: unknown, organisation?: string | null) =>
    apiRequest<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
      organisation
    }),
  delete: <T>(path: string, organisation?: string | null) =>
    apiRequest<T>(path, { method: "DELETE", organisation })
};


export async function openAuthenticated(
  path: string,
  options: { download?: boolean; filename?: string } = {}
) {
  const token = authStore.getToken();
  const organisation = authStore.getOrganisation();
  const popup = options.download ? null : window.open("about:blank", "_blank");
  const headers = new Headers({ Accept: "*/*" });
  if (token) headers.set("Authorization", `Token ${token}`);
  if (organisation) headers.set("X-Organisation-ID", organisation);

  const response = await fetch(path, { headers, credentials: "same-origin" });
  if (!response.ok) {
    if (popup) popup.close();
    const payload = await parseResponse(response);
    throw new ApiError(response.status, payload);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  if (options.download) {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = options.filename || "download";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } else if (popup) {
    popup.location.href = url;
  } else {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.target = "_blank";
    anchor.rel = "noreferrer";
    anchor.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function downloadPdf(path: string, filename: string) {
  return openAuthenticated(path, { download: true, filename });
}
